import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EpisodeExportFilters,
  deleteEpisodeCache,
  exportEpisodeTableCsv,
  exportEpisodeTableXlsx,
  getEpisodeSummary,
  getEpisodeTable,
  listEpisodes,
} from '../api/episodeDbCache';

export const useEpisodeList = (from: string, to: string) =>
  useQuery({
    queryKey: ['episode-db-cache', 'list', from, to],
    queryFn: () => listEpisodes(from, to),
    enabled: Boolean(from && to),
  });

export const useEpisodeSummary = (s3Key: string | undefined) =>
  useQuery({
    queryKey: ['episode-db-cache', 'summary', s3Key],
    queryFn: () => getEpisodeSummary(s3Key as string),
    enabled: Boolean(s3Key),
    staleTime: 5 * 60 * 1000,
  });

export const useEpisodeTable = (
  s3Key: string | undefined,
  table: string | undefined,
  opts: { limit?: number; offset?: number } = {},
) =>
  useQuery({
    queryKey: ['episode-db-cache', 'table', s3Key, table, opts.limit, opts.offset],
    queryFn: () => getEpisodeTable(s3Key as string, table as string, opts),
    enabled: Boolean(s3Key && table),
    staleTime: 5 * 60 * 1000,
  });

interface ExportArgs {
  s3Key: string;
  table: string;
  filters: EpisodeExportFilters;
}

export const useExportEpisodeTableCsv = () =>
  useMutation({
    mutationFn: ({ s3Key, table, filters }: ExportArgs) =>
      exportEpisodeTableCsv(s3Key, table, filters),
  });

export const useExportEpisodeTableXlsx = () =>
  useMutation({
    mutationFn: ({ s3Key, table, filters }: ExportArgs) =>
      exportEpisodeTableXlsx(s3Key, table, filters),
  });

export const useDeleteEpisodeCache = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (s3Key: string) => deleteEpisodeCache(s3Key),
    onSuccess: (_data, s3Key) => {
      queryClient.invalidateQueries({ queryKey: ['episode-db-cache', 'list'] });
      queryClient.removeQueries({ queryKey: ['episode-db-cache', 'summary', s3Key] });
      queryClient.removeQueries({ queryKey: ['episode-db-cache', 'table', s3Key] });
    },
  });
};
